import { Link, useLocation } from "react-router-dom";
import logo from "../../assets/logo.png";

export default function Footer() {
    const location = useLocation();
    
    // Check if we're on a product detail page (has sticky add-to-cart bar)
    const isProductPage = location.pathname.startsWith('/product/');
    
    const footerSections = [
        {
            title: "Products",
            links: [
                { name: "All Products", href: "/products" },
                { name: "Hi-Vis Clothing", href: "/products?category=hi-vis" },
                { name: "Safety Equipment", href: "/products?category=safety" },
                {
                    name: "Corporate Uniforms",
                    href: "/products?category=corporate",
                },
                { name: "Work Boots", href: "/products?category=footwear" },
            ],
        },
        {
            title: "Customer Service",
            links: [
                { name: "Contact Us", href: "/contact" },
                { name: "Returns & Exchanges", href: "/returns" },
                { name: "Shipping Info", href: "/shipping" },
                { name: "FAQ", href: "/faq" },
            ],
        },
        {
            title: "Company",
            links: [
                { name: "About Us", href: "/about" },
                { name: "Terms & Conditions", href: "/terms-and-conditions" },
                { name: "Privacy Policy", href: "/privacy-policy" },
            ],
        },
    ];

    const paymentMethods = [
        "ri-visa-line",
        "ri-mastercard-line",
        "ri-paypal-line",
        "ri-bank-card-line",
    ];

    const socialIcons = [
        { icon: "ri-facebook-fill", href: "#", label: "Facebook" },
        { icon: "ri-twitter-x-fill", href: "#", label: "Twitter" },
        { icon: "ri-linkedin-fill", href: "#", label: "LinkedIn" },
        { icon: "ri-instagram-fill", href: "#", label: "Instagram" },
    ];

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <footer className="bg-[#01164F] text-[#FEFEFE]">
            {/* Main Footer Content */}
            <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-10 md:py-14">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
                    {/* Logo & Description - Full width on mobile, 1 col on larger */}
                    <div className="col-span-2 sm:col-span-3 lg:col-span-1 mb-4 lg:mb-0">
                        <Link to="/" className="inline-block mb-4">
                            <img src={logo} alt="Logo" className="w-32 " />
                        </Link>
                        <p
                            className="text-white/70 text-sm leading-relaxed mb-4 max-w-[280px]"
                            style={{ fontFamily: "Inter, sans-serif" }}
                        >
                            Your trusted partner for quality workwear, safety
                            equipment, and corporate uniforms.
                        </p>

                        {/* Social Icons - Show here on larger screens */}
                        <div className="hidden lg:flex items-center space-x-3 mt-4">
                            {socialIcons.map((social, index) => (
                                <a
                                    key={index}
                                    href={social.href}
                                    aria-label={social.label}
                                    className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-[#F3B11A] hover:text-[#01164F] transition-all duration-200"
                                >
                                    <i
                                        className={`${social.icon} text-base`}
                                    ></i>
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Navigation Sections */}
                    {footerSections.map((section, index) => (
                        <div key={index}>
                            <h3
                                className="text-white text-sm font-semibold uppercase tracking-wide mb-3 md:mb-4"
                                style={{ fontFamily: "Poppins, sans-serif" }}
                            >
                                {section.title}
                            </h3>
                            <ul className="space-y-2">
                                {section.links.map((link, linkIndex) => (
                                    <li key={linkIndex}>
                                        <Link
                                            to={link.href}
                                            className="text-white/70 hover:text-[#F3B11A] text-sm transition-colors duration-200"
                                            style={{
                                                fontFamily: "Inter, sans-serif",
                                            }}
                                        >
                                            {link.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Mobile Social Icons & Payment */}
                <div className="lg:hidden mt-8 pt-6 border-t border-white/10">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                        {/* Social Icons */}
                        <div className="flex items-center justify-center sm:justify-start space-x-3">
                            {socialIcons.map((social, index) => (
                                <a
                                    key={index}
                                    href={social.href}
                                    aria-label={social.label}
                                    className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-[#F3B11A] hover:text-[#01164F] transition-all duration-200"
                                >
                                    <i className={`${social.icon} text-lg`}></i>
                                </a>
                            ))}
                        </div>

                        {/* Payment Methods */}
                        <div className="flex items-center justify-center sm:justify-end space-x-2">
                            {paymentMethods.map((icon, index) => (
                                <div
                                    key={index}
                                    className="w-10 h-7 bg-white rounded flex items-center justify-center"
                                >
                                    <i
                                        className={`${icon} text-[#01164F] text-base`}
                                    ></i>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Desktop Payment Methods */}
                <div className="hidden lg:block mt-8 pt-6 border-t border-white/10">
                    <div className="flex items-center justify-between">
                        <p
                            className="text-white/50 text-sm"
                            style={{ fontFamily: "Inter, sans-serif" }}
                        >
                            Accepted Payment Methods
                        </p>
                        <div className="flex items-center space-x-2">
                            {paymentMethods.map((icon, index) => (
                                <div
                                    key={index}
                                    className="w-12 h-8 bg-white rounded flex items-center justify-center"
                                >
                                    <i
                                        className={`${icon} text-[#01164F] text-lg`}
                                    ></i>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-white/10">
                <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
                        <p
                            className="text-white/50 text-xs sm:text-sm text-center sm:text-left"
                            style={{ fontFamily: "Inter, sans-serif" }}
                        >
                            © {new Date().getFullYear()} Work Wear. All rights
                            reserved.
                        </p>

                        <p
                            className="text-white/50 text-xs sm:text-sm text-center sm:text-right"
                            style={{ fontFamily: "Inter, sans-serif" }}
                        >
                            Powered by{" "}
                            <a
                                href="https://www.barodaweb.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white/70 hover:text-[#F3B11A] transition-colors duration-200"
                            >
                                BarodaWeb: The e-Catalogue Designer
                            </a>
                        </p>
                    </div>
                </div>
            </div>

            {/* Floating Back to Top Button */}
            <button
                onClick={scrollToTop}
                className={`fixed ${isProductPage ? 'bottom-20' : 'bottom-6'} right-6 w-10 h-10 bg-[#01164F] hover:bg-[#F3B11A] text-white hover:text-[#01164F] rounded-full shadow-lg flex items-center justify-center transition-all duration-200 z-50 border border-white/20`}
                aria-label="Back to top"
            >
                <i className="ri-arrow-up-line text-lg"></i>
            </button>
        </footer>
    );
}
